import { useAuthStore } from "@/store/authStore";
import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { deckService, cardProgressService } from "@/services/firestore";
import { Deck, FlashcardProgress } from "@/types";
import { Button } from "@/components/ui/button";
import { 
  Rocket, 
  Calendar, 
  Plus, 
  TrendingUp, 
  Clock,
  LayoutDashboard,
  ArrowRight,
  BookOpen,
  Star
} from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Helmet } from "react-helmet-async";

export default function DashboardPage() {
  const { user, loading } = useAuthStore();
  const navigate = useNavigate();

  const [decks, setDecks] = useState<Deck[]>([]);
  const [dueCount, setDueCount] = useState(0);
  const [masteryMap, setMasteryMap] = useState<Record<string, number>>({});
  const [totalReviews, setTotalReviews] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      const fetchStats = async () => {
        try {
          const [userDecks, allProgress] = await Promise.all([
            deckService.getDecks(user.uid),
            cardProgressService.getAllProgressForUser(user.uid)
          ]);
          setDecks(userDecks);
          
          const now = new Date();
          const due = allProgress.filter((p: FlashcardProgress) => {
            const nextDate = p.nextReview?.toDate ? p.nextReview.toDate() : new Date(p.nextReview);
            return nextDate <= now;
          }).length;
          setDueCount(due);

          // Calculate mastery per deck
          const mData: Record<string, number> = {};
          userDecks.forEach(deck => {
            const deckProgress = allProgress.filter(p => p.deckId === deck.id);
            const mastered = deckProgress.filter(p => p.easeFactor >= 2.8 && p.interval > 30).length;
            mData[deck.id!] = deck.cardCount ? Math.round((mastered / deck.cardCount) * 100) : 0;
          });
          setMasteryMap(mData);
          setTotalReviews(allProgress.reduce((acc, curr) => acc + curr.repetition, 0));
        } catch (error) {
          console.error("Error fetching dashboard stats:", error);
        } finally {
          setStatsLoading(false);
        }
      };
      fetchStats();
    }
  }, [user]);

  if (loading) return (
    <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );

  if (!user) return null;

  return (
    <div className="space-y-10 max-w-6xl mx-auto py-8 md:py-12 px-4 md:px-6">
      <Helmet>
        <title>Dashboard | Flashcard Platform</title>
      </Helmet>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight">Chào mừng quay lại, {user.displayName || "bạn học"}!</h1>
          <p className="text-xl text-muted-foreground font-medium mt-1">Chúc bạn một ngày học tập hiệu quả.</p>
        </div>
        <Link 
          to="/decks/new" 
          className={cn(buttonVariants({ size: "lg", className: "rounded-2xl shadow-lg shadow-primary/20" }), "gap-2")}
        >
          <Plus className="h-5 w-5" /> Tạo bộ thẻ mới
        </Link>
      </div>

      {/* Bento Grid Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-4 h-full md:h-[300px]">
        {/* Main Due Card */}
        <Card className="md:col-span-2 md:row-span-2 bg-primary text-primary-foreground border-none shadow-2xl shadow-primary/30 relative overflow-hidden group">
          <Rocket className="absolute -bottom-6 -right-6 h-40 w-40 text-white/10 group-hover:scale-110 transition-transform duration-700" />
          <CardContent className="p-8 h-full flex flex-col justify-between relative z-10">
            <div>
              <p className="text-primary-foreground/80 font-bold uppercase tracking-widest text-sm mb-2">Cần ôn tập ngay</p>
              <h2 className="text-7xl font-black">{dueCount}</h2>
              <p className="text-xl font-medium mt-2">Thẻ đang chờ bạn</p>
            </div>
            <Link 
              to={decks.length > 0 ? `/decks/${decks[0].id}/study` : "/marketplace"} 
              className={cn(buttonVariants({ variant: "secondary", size: "lg", className: "w-fit rounded-xl font-bold bg-white text-primary hover:bg-white/90" }))}
            >
              Học ngay bây giờ <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </CardContent>
        </Card>

        {/* Small Stat Cards */}
        {[
          { icon: TrendingUp, label: "Chuỗi ngày", value: "3 ngày", color: "text-orange-500", bg: "bg-orange-500/10" },
          { icon: Calendar, label: "Bộ thẻ", value: decks.length.toString(), color: "text-emerald-500", bg: "bg-emerald-500/10" },
          { icon: Clock, label: "Tổng số lượt ôn", value: totalReviews.toString(), color: "text-indigo-500", bg: "bg-indigo-500/10" },
          { icon: Star, label: "Cấp độ", value: "Pro", color: "text-amber-500", bg: "bg-amber-500/10" }
        ].map((stat, i) => (
          <Card key={i} className="border-2 hover:border-primary/30 transition-all group rounded-2xl">
            <CardContent className="p-6 flex flex-col justify-between h-full">
              <div className={`${stat.bg} ${stat.color} p-2.5 rounded-xl w-fit group-hover:scale-110 transition-transform`}>
                <stat.icon className="h-5 w-5" />
              </div>
              <div className="mt-4">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{stat.label}</p>
                <p className="text-2xl font-black mt-0.5">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content: Recent Decks */}
        <Card className="lg:col-span-2 border-2 rounded-3xl overflow-hidden shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between bg-muted/20 pb-8">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold">Bộ thẻ gần đây</CardTitle>
              <CardDescription className="text-base">Các bộ thẻ bạn vừa ôn tập.</CardDescription>
            </div>
            <Link 
              to="/decks" 
              className={cn(buttonVariants({ variant: "ghost", size: "sm", className: "font-bold text-primary" }))}
            >
              Xem tất cả
            </Link>
          </CardHeader>
          <CardContent className="p-8 bg-card">
            {decks.length > 0 ? (
              <div className="space-y-4">
                {decks.slice(0, 3).map((deck: Deck) => (
                  <Link 
                    key={deck.id} 
                    to={`/decks/${deck.id}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-card border hover:border-primary/50 transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <BookOpen className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <p className="font-semibold">{deck.title}</p>
                          <span className="text-[10px] font-bold text-primary px-1.5 py-0.5 rounded-full bg-primary/10">
                            {masteryMap[deck.id!] || 0}% Mastered
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{deck.cardCount || 0} cards</p>
                        <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
                           <div 
                             className="h-full bg-primary transition-all duration-500" 
                             style={{ width: `${masteryMap[deck.id!] || 0}%` }}
                           />
                        </div>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-center p-10">
                <div className="mb-6 p-6 rounded-3xl bg-muted/50 border-2 border-dashed border-muted-foreground/20">
                  <LayoutDashboard className="h-12 w-12 text-muted-foreground/30" />
                </div>
                <h3 className="font-bold text-2xl mb-2">Chưa có bộ thẻ nào</h3>
                <p className="text-muted-foreground mb-8 max-w-sm text-lg">
                  Bạn chưa tạo hoặc sao chép bộ thẻ nào. Hãy bắt đầu hành trình học tập ngay!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                  <Link to="/marketplace" className={cn(buttonVariants({ variant: "outline", size: "lg", className: "rounded-2xl px-8" }))}>Chợ cộng đồng</Link>
                  <Link to="/decks/new" className={cn(buttonVariants({ size: "lg", className: "rounded-2xl px-8 shadow-lg shadow-primary/20" }))}>Tạo bộ thẻ</Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sidebar: Activity/Reminders */}
        <Card className="border-2 rounded-3xl shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-bold">Hoạt động gần đây</CardTitle>
            <CardDescription>Tiến độ học tập của bạn.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <div className="p-5 rounded-2xl bg-primary/5 border-2 border-primary/10">
              <p className="text-sm font-bold text-primary mb-2 uppercase tracking-wider">Mục tiêu tuần</p>
              <div className="h-3 w-full bg-primary/10 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-primary w-0 transition-all duration-1000" />
              </div>
              <p className="text-sm text-muted-foreground font-medium">Đã hoàn thành 0/5 ngày tuần này.</p>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-xs font-black uppercase tracking-widest text-muted-foreground/60">Hỗ trợ học tập</h4>
              <div className="flex flex-col gap-3">
                <Button variant="outline" size="lg" className="justify-start text-sm h-12 rounded-xl border-2 hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-all font-semibold">Cơ chế SRS hoạt động thế nào?</Button>
                <Button variant="outline" size="lg" className="justify-start text-sm h-12 rounded-xl border-2 hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-all font-semibold">Làm chủ thuật toán SM-2</Button>
                <Button variant="outline" size="lg" className="justify-start text-sm h-12 rounded-xl border-2 hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-all font-semibold">Nhập dữ liệu từ CSV</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
